import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { AuroraBackground } from "@/components/AuroraBackground";
import { AuroraOrb } from "@/components/AuroraOrb";
import { Logo } from "@/components/Brand";
import { Footer } from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { unlockAudio } from "@/lib/audio";
import { useAppConfig } from "@/lib/config";

function googleLogin(invite, accepted) {
  if (invite) sessionStorage.setItem("aurora_invite", invite);
  if (accepted) sessionStorage.setItem("aurora_terms", "1");
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/app";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

const Shell = ({ title, subtitle, children }) => (
  <div className="min-h-screen flex flex-col">
    <AuroraBackground />
    <header className="px-4 sm:px-8 py-4"><Logo /></header>
    <main className="flex-1 flex items-center justify-center px-4 py-6">
      <div className="w-full max-w-md hud-card hud-corners p-6 sm:p-8">
        <div className="flex justify-center -mt-2 mb-2"><AuroraOrb size={120} hud={false} testId="auth-orb" /></div>
        <h1 className="font-heading text-3xl font-bold text-white text-center tracking-wide">{title}</h1>
        <p className="text-center text-sm text-slate-400 mt-1 mb-6">{subtitle}</p>
        {children}
      </div>
    </main>
    <Footer />
  </div>
);

const GoogleBtn = ({ invite, accepted, disabled }) => (
  <button type="button" onClick={() => googleLogin(invite, accepted)} disabled={disabled} data-testid="btn-google-login"
    className="w-full btn-ghost-hud rounded-xl py-3 text-sm font-medium flex items-center justify-center gap-2.5 disabled:opacity-40 disabled:cursor-not-allowed">
    <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
    Continua con Google
  </button>
);

const TermsCheckbox = ({ checked, onChange }) => (
  <label className="flex items-start gap-2.5 mb-4 cursor-pointer text-xs text-slate-300 leading-relaxed" data-testid="register-terms-label">
    <Checkbox checked={checked} onCheckedChange={(v) => onChange(!!v)} data-testid="register-terms-checkbox" className="mt-0.5 border-cyan-400/60 data-[state=checked]:bg-cyan-400 data-[state=checked]:text-[#031018]" />
    <span>Ho letto e accetto i <Link to="/termini-e-condizioni" target="_blank" className="text-cyan-300 underline">Termini e Condizioni</Link> e la <Link to="/privacy-policy" target="_blank" className="text-cyan-300 underline">Privacy Policy</Link> di PIXAWEB Sagl. <span className="text-rose-300">*</span></span>
  </label>
);

const Divider = () => (
  <div className="flex items-center gap-3 my-5"><span className="h-px flex-1 bg-cyan-500/20" /><span className="hud-tag !text-slate-500">oppure</span><span className="h-px flex-1 bg-cyan-500/20" /></div>
);

const Field = ({ id, label, ...p }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-xs font-mono uppercase tracking-[0.15em] text-slate-400">{label}</Label>
    <Input id={id} {...p} className="bg-[#050b18]/80 border-cyan-500/25 h-11 focus-visible:ring-cyan-400/50" />
  </div>
);

export function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    unlockAudio();
    setBusy(true);
    try {
      await login(f.email, f.password);
      nav("/app");
    } catch (err) {
      toast.error(errMsg(err, "Accesso non riuscito"));
    } finally { setBusy(false); }
  };
  return (
    <Shell title="Bentornato" subtitle="Accedi per riattivare Aurora">
      <GoogleBtn />
      <p className="text-[11px] text-slate-500 text-center mt-2">Continuando con Google accetti i <Link to="/termini-e-condizioni" className="text-cyan-300">Termini e Condizioni</Link> e la <Link to="/privacy-policy" className="text-cyan-300">Privacy Policy</Link>.</p>
      <Divider />
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <Field id="email" label="Email" type="text" autoComplete="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="login-email-input" />
        <Field id="password" label="Password" type="password" autoComplete="current-password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="login-password-input" />
        <button disabled={busy} data-testid="login-submit-button" className="w-full btn-aurora rounded-xl py-3 text-sm uppercase flex items-center justify-center gap-2 disabled:opacity-60">
          {busy && <Loader2 className="animate-spin" size={16} />} Accedi
        </button>
      </form>
      <p className="text-center text-sm text-slate-400 mt-6">Non hai un account? <Link to="/registrati" className="text-cyan-300 hover:text-amber-300" data-testid="link-to-register">Registrati</Link></p>
    </Shell>
  );
}

export function RegisterPage() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "", invite_code: params.get("ref") || "" });
  const [accepted, setAccepted] = useState(false);
  const { subscriptions_enabled: paid } = useAppConfig();
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!accepted) { toast.error("Devi accettare i Termini e Condizioni e la Privacy Policy"); return; }
    unlockAudio();
    setBusy(true);
    try {
      await register({ ...f, invite_code: f.invite_code || null, accept_terms: true });
      nav("/app");
    } catch (err) {
      toast.error(errMsg(err, "Registrazione non riuscita"));
    } finally { setBusy(false); }
  };
  return (
    <Shell title="Crea il tuo account" subtitle={paid ? "30 giorni di prova gratuita inclusi" : "Accesso completo e gratuito"}>
      <TermsCheckbox checked={accepted} onChange={setAccepted} />
      <GoogleBtn invite={f.invite_code} accepted={accepted} disabled={!accepted} />
      <Divider />
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <Field id="name" label="Nome" required value={f.name} onChange={set("name")} data-testid="register-name-input" />
        <Field id="remail" label="Email" type="email" required value={f.email} onChange={set("email")} data-testid="register-email-input" />
        <Field id="phone" label="Numero di telefono" type="tel" required placeholder="+39 333 1234567" value={f.phone} onChange={set("phone")} data-testid="register-phone-input" />
        <Field id="rpassword" label="Password (min 6 caratteri)" type="password" minLength={6} required value={f.password} onChange={set("password")} data-testid="register-password-input" />
        <Field id="invite" label="Codice invito (opzionale)" value={f.invite_code} onChange={set("invite_code")} data-testid="register-invite-input" />
        <button disabled={busy || !accepted} data-testid="register-submit-button" className="w-full btn-aurora rounded-xl py-3 text-sm uppercase flex items-center justify-center gap-2 disabled:opacity-60">
          {busy && <Loader2 className="animate-spin" size={16} />} Attiva Aurora
        </button>
      </form>
      <p className="text-center text-sm text-slate-400 mt-6">Hai già un account? <Link to="/accedi" className="text-cyan-300 hover:text-amber-300" data-testid="link-to-login">Accedi</Link></p>
    </Shell>
  );
}
